import { useState, useCallback } from 'react';

/**
 * Platform-agnostic hook for managing authentication modal states.
 * Handles verification, invite-only, and failure notification modals.
 */
export interface UseAuthModalsResult {
  verification: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  inviteOnly: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  forgotPassword: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  failureNotification: {
    isOpen: boolean;
    message: string;
    open: (message: string) => void;
    close: () => void;
  };
}

export function useAuthModals(): UseAuthModalsResult {
  const [showVerification, setShowVerification] = useState(false);
  const [showInviteOnly, setShowInviteOnly] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  const openVerification = useCallback(() => setShowVerification(true), []);
  const closeVerification = useCallback(() => setShowVerification(false), []);

  const openInviteOnly = useCallback(() => setShowInviteOnly(true), []);
  const closeInviteOnly = useCallback(() => setShowInviteOnly(false), []);

  const openForgotPassword = useCallback(() => setShowForgotPassword(true), []);
  const closeForgotPassword = useCallback(() => setShowForgotPassword(false), []);

  const openFailure = useCallback((message: string) => {
    setFailureMessage(message);
    setShowFailure(true);
  }, []);
  const closeFailure = useCallback(() => setShowFailure(false), []);

  return {
    verification: {
      isOpen: showVerification,
      open: openVerification,
      close: closeVerification,
    },
    inviteOnly: {
      isOpen: showInviteOnly,
      open: openInviteOnly,
      close: closeInviteOnly,
    },
    forgotPassword: {
      isOpen: showForgotPassword,
      open: openForgotPassword,
      close: closeForgotPassword,
    },
    failureNotification: {
      isOpen: showFailure,
      message: failureMessage,
      open: openFailure,
      close: closeFailure,
    },
  };
}
